import { AlertTriangle } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import { Button, Dialog } from './ui';

type Warning = { code: string; message: string };
interface Pending {
  title: string;
  warnings: Warning[];
  confirmLabel: string;
  resolve: (ok: boolean) => void;
}

const Ctx = createContext<((title: string, warnings: Warning[], confirmLabel?: string) => Promise<boolean>) | null>(null);

/**
 * The API answers soft business checks ("room not inspected", "deposit
 * outstanding") with CONFIRMATION_REQUIRED. This provider shows them and lets
 * an authorised user proceed, re-submitting with the acknowledged codes.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const confirm = useCallback(
    (title: string, warnings: Warning[], confirmLabel = 'Proceed anyway') =>
      new Promise<boolean>((resolve) => setPending({ title, warnings, confirmLabel, resolve })),
    [],
  );
  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  return (
    <Ctx.Provider value={confirm}>
      {children}
      <Dialog
        open={!!pending}
        onOpenChange={(o) => !o && close(false)}
        title={pending?.title ?? ''}
        footer={
          <>
            <Button onClick={() => close(false)}>Go back</Button>
            <Button variant="primary" onClick={() => close(true)}>
              {pending?.confirmLabel}
            </Button>
          </>
        }
      >
        <ul className="space-y-2">
          {pending?.warnings.map((w) => (
            <li key={w.code} className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
              {w.message}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">Your acknowledgement is recorded in the audit trail.</p>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useConfirm outside ConfirmProvider');
  return c;
}

/** Run an API call; if the server asks for confirmation, ask the user and retry with acknowledgements. */
export function useAcknowledged() {
  const confirm = useConfirm();
  const busy = useRef(false);
  return useCallback(
    async <T,>(title: string, run: (acknowledge: string[]) => Promise<T>): Promise<T | undefined> => {
      if (busy.current) return undefined;
      busy.current = true;
      try {
        const ack: string[] = [];
        for (let i = 0; i < 4; i++) {
          try {
            return await run(ack);
          } catch (e) {
            if (!(e instanceof ApiError) || e.code !== 'CONFIRMATION_REQUIRED') throw e;
            if (!(await confirm(title, e.warnings))) return undefined;
            ack.push(...e.warnings.map((w) => w.code));
          }
        }
        return undefined;
      } finally {
        busy.current = false;
      }
    },
    [confirm],
  );
}
